import { Injectable } from '@angular/core';
import { GridHeader } from '../../sample-tracking/tracking-overview/interfaces/grid';
import * as XLSX from 'xlsx';
import { DateRangeFilterValue } from '../../shared/time-frame-filter/timeframe-filter.component';

@Injectable()
export class ProtocolShipmentService {

    public gridHeader() {
        return [
            {
                displayText: 'Accession',
                key: 'accessionNumber',
                sort: null,
                enableFilter: true,
                enableSearch: true
            },
            {
                displayText: 'Container Number',
                key: 'containerNumber',
                sort: null,
                enableFilter: true,
                enableSearch: true
            },
            {
                displayText: 'Container Barcode',
                key: 'barcode',
                sort: null,
                enableFilter: true,
                enableSearch: true
            },
            {
                displayText: 'Visit',
                key: 'visitDescription',
                sort: null,
                enableFilter: true,
                enableSearch: true
            },
            {
                displayText: 'Container Label Line 1',
                key: 'containerLabel1',
                sort: null,
                enableFilter: true,
                enableSearch: true
            },
            {
                displayText: 'Container Label Line 2',
                key: 'containerLabel2',
                sort: null,
                enableFilter: true,
                enableSearch: true
            },
            {
                displayText: 'Expected Return Condition',
                key: 'expectedReturnCondition',
                sort: null,
                enableFilter: true,
                enableSearch: true
            },
            {
                displayText: 'Shipped To Organization Name',
                key: 'toOrganizationName',
                sort: null,
                enableFilter: true,
                enableSearch: true
            },
            {
                displayText: 'Waybill Number',
                key: 'trackingNumber',
                sort: null,
                enableFilter: true,
                enableSearch: true
            },
            {
                displayText: 'Courier',
                key: 'courierName',
                sort: null,
                enableFilter: true,
                enableSearch: true
            },
            {
                displayText: 'Shipment Submitted Date',
                key: 'shipmentSubmittedDate',
                sort: null,
                enableFilter: true,
                enableSearch: true,
                skipConversion: true,
                isDate: true
            },
            {
                displayText: 'Shipment Submitted By',
                key: 'shipmentSubmittedBy',
                sort: null,
                enableFilter: true,
                enableSearch: true
            },
            {
                displayText: 'Sample Status',
                key: 'sampleStatus',
                sort: null,
                enableFilter: true,
                enableSearch: true
            },
            {
                displayText: 'Screening Number',
                key: 'screeningNumber',
                sort: null,
                enableFilter: true,
                enableSearch: true
            },
            {
                displayText: 'Patient Number',
                key: 'patientNumber',
                sort: null,
                enableFilter: true,
                enableSearch: true
            },
            {
                displayText: 'Collection Date',
                key: 'collectionDate',
                sort: null,
                isDate: true,
                enableFilter: true,
                skipConversion: true,
                enableSearch: true
            },
            {
                displayText: 'Protocol Visit Code',
                key: 'protocolVisitCode',
                sort: null,
                enableFilter: true,
                enableSearch: true
            },
            {
                displayText: 'Kit Receipt Date',
                key: 'kitReceiptDate',
                sort: null,
                enableFilter: true,
                skipConversion: true,
                enableSearch: true
            },
            {
                displayText: 'Container Receipt Date',
                key: 'containerReceiptDate',
                sort: null,
                enableFilter: true,
                skipConversion: true,
                enableSearch: true
            },
            {
                displayText: 'Actual Return Condition',
                key: 'actualReturnCondition',
                sort: null,
                enableFilter: true,
                enableSearch: true,
            },
            {
                displayText: 'Delivery Status',
                key: 'shipmentStatus',
                sort: null,
                enableFilter: true,
                enableSearch: true,
                isLink: true,
                defaultValue: 'N/A'
            }
        ] as GridHeader[];
    }

    public exportData(
        gridColumns: GridHeader[], rawData: ProtocolGridData[], fileName: string,
        downloadAsXlsx: boolean, headerText: string, timeRange: DateRangeFilterValue) {
        if (!rawData || rawData.length === 0) { return; }
        let header = gridColumns.map(t => t.displayText);

        const downloadData = this.getMappedData(rawData);
        const wb = XLSX.utils.book_new();
        const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet([]);
        XLSX.utils.sheet_add_aoa(ws, [[headerText, 'TimeRange From : ' + timeRange.dateBegin + ' To : ' + timeRange.dateEnd]]);
        XLSX.utils.sheet_add_aoa(ws, [header], { origin: 'A2' });
        XLSX.utils.sheet_add_json(ws, downloadData, { origin: 'A3', skipHeader: true });
        XLSX.utils.book_append_sheet(wb, ws);
        XLSX.writeFile(wb, `${fileName}${downloadAsXlsx ? '.xlsx' : '.csv'}`);
    }

    private getMappedData(gridData: ProtocolGridData[]) {
        return gridData.map((data) => {
            return {
                accessionNumber: data.accessionNumber,
                containerNumber: data.containerNumber,
                containerBarcode: data.barcode,
                visitDescription: data.visitDescription,
                containerLabelLine1: data.containerLabel1,
                containerLabelLine2: data.containerLabel2,
                expectedReturnCondition: data.expectedReturnCondition,
                shippedToOrganizationName: data.toOrganizationName,
                airwayBillNumber: data.trackingNumber,
                courier: data.courierName,
                shipmentSubmittedDate: data.shipmentSubmittedDate,
                shipmentSubmittedBy: data.shipmentSubmittedBy,
                sampleStatus: data.sampleStatus,
                screeningNumber: data.screeningNumber,
                patientNumber: data.patientNumber,
                collectionDate: data.collectionDate,
                protocolVisitCode: data.protocolVisitCode,
                kitReceiptDate: data.kitReceiptDate,
                containerReceiptDate: data.containerReceiptDate,
                actualReturnCondition: data.actualReturnCondition,
                shipmentStatus: data.shipmentStatus
            };
        });
    }
}

export interface ProtocolGridData {
    accessionId: number;
    accessionNumber: string;
    actualReturnCondition: string;
    barcode: string;
    collectionDate: string;
    containerID: number;
    containerLabel1: string;
    containerLabel2: string;
    containerNumber: string;
    containerReceiptDate: string;
    courierName: string;
    expectedReturnCondition: string;
    kitReceiptDate: string;
    patientNumber: string;
    protocolVisitCode: string;
    sampleStatus: string;
    screeningNumber: string;
    shipmentId: number;
    shipmentSubmittedBy: string;
    shipmentSubmittedDate: string;
    toOrganizationName: string;
    trackingNumber: string;
    visitDescription: string;
    shipmentStatus: string;
}